import {
  Button,
  Callout,
  FormGroup,
  HTMLTable,
  InputGroup,
  Tooltip,
} from "@blueprintjs/core";
import { useState } from "react";
import { useAdminQuery } from "../../api/AdminClient";
import { useMutation } from "../../api/useMutation";
import { AdminErrorMessage } from "../../shared/AdminErrorMessage";
import type { GeneratorFixtureUser } from "./fixtureModels";

interface CustomerLookupResult {
  customerNumber: string;
  customerGuid: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  dateOfBirth: string | null;
}

interface PendingUser {
  customerNumber: string;
  customerGuid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  dateOfBirth: string;
}

interface SaveGeneratorFixtureUserRequest {
  customerNumber: string;
  customerGuid: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  dateOfBirth: string | null;
}

function toPending(result: CustomerLookupResult): PendingUser {
  return {
    customerNumber: result.customerNumber,
    customerGuid: result.customerGuid,
    firstName: result.firstName ?? "",
    lastName: result.lastName ?? "",
    email: result.email ?? "",
    phone: result.phone ?? "",
    address: result.address ?? "",
    postalCode: result.postalCode ?? "",
    city: result.city ?? "",
    dateOfBirth: result.dateOfBirth ?? "",
  };
}

const USERS_PATH = "admin/reportGenerator/fixtures/users";

function DeleteUserButton({
  id,
  onDeleted,
}: {
  id: string;
  onDeleted: () => void;
}) {
  const remove = useMutation<undefined, void>({
    path: `${USERS_PATH}/${id}`,
    method: "DELETE",
  });

  return (
    <Tooltip content="Remove this saved user from the pool">
      <Button
        icon="trash"
        type="button"
        minimal
        loading={remove.result.loading}
        onClick={async () => {
          await remove.fetch();
          onDeleted();
        }}
      />
    </Tooltip>
  );
}

export function GeneratorUsersPanel() {
  const [customerNumber, setCustomerNumber] = useState("");
  const [pending, setPending] = useState<PendingUser | null>(null);

  const list = useAdminQuery<GeneratorFixtureUser[]>(USERS_PATH);

  const lookup = useMutation<undefined, CustomerLookupResult>({
    path: `admin/reportGenerator/customerLookup?customerNumber=${encodeURIComponent(customerNumber)}`,
    method: "GET",
  });

  const save = useMutation<SaveGeneratorFixtureUserRequest, GeneratorFixtureUser>({
    path: USERS_PATH,
    method: "POST",
    body: pending
      ? {
          customerNumber: pending.customerNumber,
          customerGuid: pending.customerGuid,
          firstName: pending.firstName || null,
          lastName: pending.lastName || null,
          email: pending.email || null,
          phone: pending.phone,
          address: pending.address || null,
          postalCode: pending.postalCode || null,
          city: pending.city || null,
          dateOfBirth: pending.dateOfBirth || null,
        }
      : undefined,
  });

  async function onLookup() {
    if (!customerNumber) return;
    const result = await lookup.fetch();
    if (result) setPending(toPending(result));
  }

  function updatePending(field: keyof PendingUser, value: string) {
    if (!pending) return;
    setPending({ ...pending, [field]: value });
  }

  async function onSave() {
    if (!pending) return;
    const saved = await save.fetch();
    if (saved) {
      setPending(null);
      setCustomerNumber("");
      list.fetch({ path: USERS_PATH });
    }
  }

  return (
    <div>
      <h3>Look up and save a user</h3>
      <FormGroup label="Customer number" labelFor="usersCustomerNumber" inline>
        <InputGroup
          id="usersCustomerNumber"
          disabled={lookup.result.loading}
          value={customerNumber}
          onChange={(e) => setCustomerNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onLookup();
          }}
          rightElement={
            <Button
              type="button"
              disabled={!customerNumber}
              loading={lookup.result.loading}
              onClick={onLookup}
            >
              Hent opplysninger
            </Button>
          }
        />
      </FormGroup>
      {lookup.result.error ? (
        <AdminErrorMessage>{lookup.result.error.message}</AdminErrorMessage>
      ) : null}
      {pending ? (
        <>
          <FormGroup label="First name" labelFor="userFirstName" inline>
            <InputGroup id="userFirstName" value={pending.firstName} onChange={(e) => updatePending("firstName", e.target.value)} />
          </FormGroup>
          <FormGroup label="Last name" labelFor="userLastName" inline>
            <InputGroup id="userLastName" value={pending.lastName} onChange={(e) => updatePending("lastName", e.target.value)} />
          </FormGroup>
          <FormGroup label="Email" labelFor="userEmail" inline>
            <InputGroup id="userEmail" value={pending.email} onChange={(e) => updatePending("email", e.target.value)} />
          </FormGroup>
          <FormGroup label="Phone" labelFor="userPhone" inline>
            <InputGroup id="userPhone" value={pending.phone} onChange={(e) => updatePending("phone", e.target.value)} />
          </FormGroup>
          <FormGroup label="Address" labelFor="userAddress" inline>
            <InputGroup id="userAddress" value={pending.address} onChange={(e) => updatePending("address", e.target.value)} />
          </FormGroup>
          <FormGroup label="Postal code" labelFor="userPostalCode" inline>
            <InputGroup id="userPostalCode" value={pending.postalCode} onChange={(e) => updatePending("postalCode", e.target.value)} />
          </FormGroup>
          <FormGroup label="City" labelFor="userCity" inline>
            <InputGroup id="userCity" value={pending.city} onChange={(e) => updatePending("city", e.target.value)} />
          </FormGroup>
          <FormGroup label="Date of birth" labelFor="userDateOfBirth" inline>
            <InputGroup
              id="userDateOfBirth"
              type="date"
              value={pending.dateOfBirth}
              onChange={(e) => updatePending("dateOfBirth", e.target.value)}
            />
          </FormGroup>
          <Button
            type="button"
            disabled={!pending.phone}
            loading={save.result.loading}
            onClick={onSave}
          >
            Save to pool
          </Button>
        </>
      ) : null}
      {save.result.error ? (
        <AdminErrorMessage>{save.result.error.message}</AdminErrorMessage>
      ) : null}

      <h3>Saved users</h3>
      {list.error ? (
        <AdminErrorMessage>{list.error.message}</AdminErrorMessage>
      ) : null}
      {list.data && list.data.length === 0 ? (
        <Callout>No saved users yet.</Callout>
      ) : null}
      {list.data && list.data.length > 0 ? (
        <HTMLTable striped>
          <thead>
            <tr>
              <th>Name</th>
              <th>Customer number</th>
              <th>Phone</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.map((user) => (
              <tr key={user.customerGuid}>
                <td>
                  {user.firstName} {user.lastName}
                </td>
                <td>{user.customerNumber}</td>
                <td>{user.phone}</td>
                <td>
                  <DeleteUserButton id={user.customerGuid} onDeleted={() => list.fetch({ path: USERS_PATH })} />
                </td>
              </tr>
            ))}
          </tbody>
        </HTMLTable>
      ) : null}
    </div>
  );
}
